import React, {FC} from 'react';
import styled from 'styled-components';

import {directHoverSelector} from '../../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {CommentAnnotationModel} from '../../../../../libs/shared/core/src/models/api/annotationModel';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {ViewerPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisuallyHidden} from '../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {CommentModel} from '../../../../models/api/commentModel';
import {CommentSummary} from '../../../common/referencedActivity/commentSummary';
import {renderDraftReference} from '../activity/basic/referencedActivityTextHelpers';

/*
 * Props.
 */

interface CommentComposerReplyPreviewProps {
  annotation?: CommentAnnotationModel;
  commentBeingRepliedTo?: CommentModel;
  authorBeingRepliedTo?: TeammateModel;
  onRemoveReplyClick?: () => void;
  userId: number;
  mentionableAliases: ReadonlySet<string>;
  panelId: ViewerPanelId;
}

/*
 * Style.
 */

const StyledReplyPreviewDiv = styled.div`
  flex: none;
  margin: 3px 3px;
  padding: 6px 10px;
  background-color: ${(p) => p.theme.appBackground};
  border-radius: 12px;
  position: relative;
`;

const StyledTitleRow = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 1px;
  height: 14px;
`;

const StyledTitleDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.semibold};
  margin-left: 4px;
`;

const StyledHoverDiv = styled.div`
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  div:hover > && {
    display: flex;
  }
`;

const StyledActionButton = styled(ChromelessButton)`
  position: absolute;
  right: 3px;
  top: 3px;
`;

const StyledActionDiv = styled.div`
  padding: 2px;
  border-radius: 50%;
  background: ${(p) => p.theme.palette.blue.shade40};

  ${directHoverSelector} {
    background: ${(p) => p.theme.palette.blue.shade50};
  }
`;

/*
 * Component.
 */

export const CommentComposerReplyPreview: FC<CommentComposerReplyPreviewProps> = (props) => {
  if (!props.commentBeingRepliedTo || !props.authorBeingRepliedTo) {
    return null;
  }

  return (
    <StyledReplyPreviewDiv>
      <StyledHoverDiv>{maybeRenderRemoveButton(props)}</StyledHoverDiv>
      <StyledTitleRow>
        <Icon name="replyArrow" />
        <StyledTitleDiv>
          {renderDraftReference(props.authorBeingRepliedTo, props.userId, props.panelId)}
        </StyledTitleDiv>
      </StyledTitleRow>
      <CommentSummary
        annotation={props.annotation}
        comment={props.commentBeingRepliedTo}
        mentionableAliases={props.mentionableAliases}
        shouldTruncateAnnotationText
      />
    </StyledReplyPreviewDiv>
  );
};

const maybeRenderRemoveButton = (props: CommentComposerReplyPreviewProps) => {
  if (!props.onRemoveReplyClick) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledActionButton shouldPreventMouseDown onClick={props.onRemoveReplyClick}>
          <StyledActionDiv>
            <Icon name="exitTiny" color={theme.greys.white} />
            <VisuallyHidden>
              <Trans id="remove-annotation">Remove Annotation</Trans>
            </VisuallyHidden>
          </StyledActionDiv>
        </StyledActionButton>
      )}
    </UserThemeConsumer>
  );
};
